import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { MatDialog } from "@angular/material/dialog";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { EventEmitterService } from "src/app/Services/event-emitter.service";

export interface CollaborationInvite {
  partitionKey?: string;
  rowKey?: string;
  flowId: string;
  flowTitle?: string;
  inviterEmail?: string;
  inviterUserName?: string;
  inviterProfilePicUrl?: string;
  inviteeEmail?: string;
  inviteeUserName?: string;
  inviteeProfilePicUrl?: string;
  inviteMessage?: string;
  collaborationValue?: number;
  responseMessage?: string;
  status: "Pending" | "Accepted" | "Rejected";
  invitedAt?: string;
  respondedAt?: string;
}

@Component({
  selector: "app-collaboration-requests",
  templateUrl: "./collaboration-requests.component.html",
  styleUrls: ["./collaboration-requests.component.scss"],
})
export class CollaborationRequestsComponent implements OnInit {
  userData: any;
  // Tab state
  activeTab: "incoming" | "outgoing" = "incoming";

  // Data
  incomingRequests: CollaborationInvite[] = [];
  outgoingRequests: CollaborationInvite[] = [];
  filteredIncoming: CollaborationInvite[] = [];
  filteredOutgoing: CollaborationInvite[] = [];

  // Loading states
  isLoadingIncoming = false;
  isLoadingOutgoing = false;

  // Search / filter
  incomingSearch = "";
  outgoingSearch = "";
  incomingStatusFilter = "All";
  outgoingStatusFilter = "All";

  // View mode
  viewMode: "card" | "table" = "card";

  // Preview panel
  previewRequest: CollaborationInvite | null = null;
  isPreviewOpen = false;

  // Response dialog (inline)
  actionRequest: CollaborationInvite | null = null;
  actionType: "approve" | "reject" | "withdraw" | null = null;
  actionMessage = "";
  isActioning = false;

  readonly statusOptions = ["All", "Pending", "Accepted", "Rejected"];

  constructor(
    private flowService: FlowServiceService,
    private toastr: ToastrService,
    private router: Router,
    private eventEmitter: EventEmitterService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.userData = JSON.parse(localStorage.getItem("LoggedInUser") || "{}");
    if (window && window.innerWidth <= 768) {
      this.viewMode = "card";
    }
    this.eventEmitter.setDashboardMenuStyleProperty(
      "collaboration-requests,Collaboration Requests"
    );
    this.loadIncoming();
    this.loadOutgoing();
  }

  // ─── Load ─────────────────────────────────────────────────────────────────

  loadIncoming(): void {
    this.isLoadingIncoming = true;
    this.flowService.getReceivedCollabInvites().subscribe(
      (data: CollaborationInvite[]) => {
        this.incomingRequests = Array.isArray(data) ? data : [];
        this.applyIncomingFilter();
        this.isLoadingIncoming = false;
      },
      () => {
        this.toastr.error("Failed to load incoming collaboration invites.");
        this.isLoadingIncoming = false;
      }
    );
  }

  loadOutgoing(): void {
    this.isLoadingOutgoing = true;
    this.flowService.getSentCollabInvites().subscribe(
      (data: CollaborationInvite[]) => {
        this.outgoingRequests = Array.isArray(data) ? data : [];
        this.applyOutgoingFilter();
        this.isLoadingOutgoing = false;
      },
      () => {
        this.toastr.error("Failed to load sent collaboration invites.");
        this.isLoadingOutgoing = false;
      }
    );
  }

  // ─── Filter ───────────────────────────────────────────────────────────────

  applyIncomingFilter(): void {
    let list = [...this.incomingRequests];
    const term = this.incomingSearch.trim().toLowerCase();
    if (term) {
      list = list.filter(
        (r) =>
          r.flowTitle?.toLowerCase().includes(term) ||
          r.inviterUserName?.toLowerCase().includes(term) ||
          r.inviterEmail?.toLowerCase().includes(term)
      );
    }
    if (this.incomingStatusFilter !== "All") {
      list = list.filter((r) => r.status === this.incomingStatusFilter);
    }
    this.filteredIncoming = list;
  }

  applyOutgoingFilter(): void {
    let list = [...this.outgoingRequests];
    const term = this.outgoingSearch.trim().toLowerCase();
    if (term) {
      list = list.filter(
        (r) =>
          r.flowTitle?.toLowerCase().includes(term) ||
          r.inviteeUserName?.toLowerCase().includes(term) ||
          r.inviteeEmail?.toLowerCase().includes(term)
      );
    }
    if (this.outgoingStatusFilter !== "All") {
      list = list.filter((r) => r.status === this.outgoingStatusFilter);
    }
    this.filteredOutgoing = list;
  }

  // ─── Stats ────────────────────────────────────────────────────────────────

  getStats(list: CollaborationInvite[]) {
    return {
      total: list.length,
      pending: list.filter((r) => r.status === "Pending").length,
      approved: list.filter((r) => r.status === "Accepted").length,
      rejected: list.filter((r) => r.status === "Rejected").length,
    };
  }

  get incomingStats() {
    return this.getStats(this.incomingRequests);
  }

  get outgoingStats() {
    return this.getStats(this.outgoingRequests);
  }

  // ─── Status helpers ───────────────────────────────────────────────────────

  getStatusClass(status: string): string {
    switch ((status || "").toLowerCase()) {
      case "pending":  return "cr-status cr-status--pending";
      case "accepted": return "cr-status cr-status--approved";
      case "rejected": return "cr-status cr-status--rejected";
      default:         return "cr-status cr-status--pending";
    }
  }

  getStatusIcon(status: string): string {
    switch ((status || "").toLowerCase()) {
      case "pending":  return "schedule";
      case "accepted": return "check_circle";
      case "rejected": return "cancel";
      default:         return "schedule";
    }
  }

  // ─── Preview ──────────────────────────────────────────────────────────────

  openPreview(request: CollaborationInvite): void {
    this.previewRequest = request;
    this.isPreviewOpen = true;
  }

  closePreview(): void {
    this.isPreviewOpen = false;
    setTimeout(() => (this.previewRequest = null), 300);
  }

  navigateToContent(request: CollaborationInvite): void {
    if (!request?.flowId) return;
    const slug = this.generateUrlSlug(request.flowTitle || "flow");
    this.router.navigate(["/guide", slug, request.flowId]);
  }

  // ─── Actions ──────────────────────────────────────────────────────────────

  openAction(
    request: CollaborationInvite,
    type: "approve" | "reject" | "withdraw"
  ): void {
    this.actionRequest = request;
    this.actionType = type;
    this.actionMessage = "";
  }

  cancelAction(): void {
    this.actionRequest = null;
    this.actionType = null;
    this.actionMessage = "";
  }

  confirmAction(): void {
    if (!this.actionRequest || !this.actionType) return;
    this.isActioning = true;

    if (this.actionType === "approve") {
      this.flowService
        .acceptCollabInvite(this.actionRequest.flowId, this.actionMessage || undefined)
        .subscribe(
          () => {
            this.toastr.success("Collaboration invite accepted.");
            this.cancelAction();
            this.loadIncoming();
            this.isActioning = false;
          },
          () => {
            this.toastr.error("Failed to accept invite.");
            this.isActioning = false;
          }
        );
    } else if (this.actionType === "reject") {
      this.flowService
        .rejectCollabInvite(this.actionRequest.flowId, this.actionMessage || undefined)
        .subscribe(
          () => {
            this.toastr.success("Collaboration invite rejected.");
            this.cancelAction();
            this.loadIncoming();
            this.isActioning = false;
          },
          () => {
            this.toastr.error("Failed to reject invite.");
            this.isActioning = false;
          }
        );
    } else if (this.actionType === "withdraw") {
      this.flowService
        .cancelCollabInvite(this.actionRequest.flowId, this.actionRequest.inviteeEmail)
        .subscribe(
          () => {
            this.toastr.success("Collaboration invite cancelled.");
            this.cancelAction();
            this.loadOutgoing();
            this.isActioning = false;
          },
          () => {
            this.toastr.error("Failed to cancel invite.");
            this.isActioning = false;
          }
        );
    }
  }

  // ─── Utilities ────────────────────────────────────────────────────────────

  formatDate(dateString: string | undefined): string {
    if (!dateString) return "—";
    const d = new Date(dateString);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  getInitial(name: string): string {
    return (name || "?").charAt(0).toUpperCase();
  }

  private generateUrlSlug(html: string): string {
    return (html || "")
      .replace(/<[^>]*>/g, "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .toLowerCase();
  }

  toggleViewMode(): void {
    this.viewMode = this.viewMode === "card" ? "table" : "card";
  }
}
